/**
 * Recent admin changes with a before/after diff and, where applicable, a rollback action
 * (Phase 6). check_schema / risk_matrix_schema / agent_config keep no separate version
 * table - every write is already {before, after} on audit_log, so rollback just replays a
 * past `before` snapshot as a new, equally-audited write (see worker/src/admin/auditLog.ts).
 * Standing instructions are reverted via "Activate" on an earlier version instead (see
 * StandingInstructionsAdmin.tsx) - not via this panel.
 */
import { useEffect, useState } from 'react';
import { fetchAuditLog } from '../../admin/queries';
import { rollbackAuditEntry, ApiError } from '../../api';
import type { AuditLogRow } from '../../admin/types';
import { Banner, Button, Chip, Spinner, Tile } from '../../components/ui';

const ROLLBACKABLE_ENTITIES = new Set(['check_schema', 'risk_matrix_schema', 'agent_config']);
const ENTITIES = ['check_schema', 'risk_matrix_schema', 'agent_config', 'standing_instructions', 'banks', 'cron_settings', 'reports'];

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-AU');
}

export function AuditLogPanel() {
  const [entries, setEntries] = useState<AuditLogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      setEntries(await fetchAuditLog(ENTITIES, 100));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the audit log.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function rollback(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await rollbackAuditEntry(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not roll back that change.');
    } finally {
      setBusyId(null);
    }
  }

  if (error && !entries) return <Banner tone="error">{error}</Banner>;
  if (!entries) return <Spinner label="Loading the audit log…" />;

  return (
    <div className="stack">
      {error ? <Banner tone="error">{error}</Banner> : null}
      <Tile className="admin-card">
        <div className="admin-rows">
          {entries.length === 0 ? <p className="muted">No admin changes yet.</p> : null}
          {entries.map((entry) => {
            const canRollback = entry.entity && ROLLBACKABLE_ENTITIES.has(entry.entity) && entry.action !== 'admin.rollback';
            const expanded = expandedId === entry.id;
            return (
              <div className="admin-row admin-row--stacked" key={entry.id}>
                <div className="admin-row__fields">
                  <span className="check-row__label">{entry.action}</span>
                  <span className="muted">
                    {formatTimestamp(entry.createdAt)} · {entry.actor ?? 'unknown'}
                    {entry.entityId ? ` · ${entry.entityId}` : ''}
                  </span>
                  {entry.entity ? <Chip>{entry.entity}</Chip> : null}
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <Button variant="ghost" onClick={() => setExpandedId(expanded ? null : entry.id)}>
                      {expanded ? 'Hide detail' : 'View detail'}
                    </Button>
                    {canRollback ? (
                      <Button variant="secondary" onClick={() => void rollback(entry.id)} disabled={busyId === entry.id}>
                        {busyId === entry.id ? 'Rolling back…' : 'Roll back'}
                      </Button>
                    ) : null}
                  </div>
                  {expanded ? <pre className="admin-detail">{JSON.stringify(entry.detail, null, 2)}</pre> : null}
                </div>
              </div>
            );
          })}
        </div>
      </Tile>
    </div>
  );
}
